import React, { useEffect, useRef } from 'react';
import PSPDFKit from '@nutrient-sdk/viewer';
import { configService } from '@web/services/singletons';
import { Constants } from '../../../../../../packages/core/config';

export const PdfHighlighter = ({
    documentUrl,
    keyword,
    pageNumber,
    highlightedWords,
}: {
    documentUrl: string;
    keyword: string;
    pageNumber: number;
    highlightedWords: string[] | undefined;
}) => {
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;

        if (!container || !highlightedWords) {
            return;
        }

        (async function () {
            await PSPDFKit.load({
                container,
                disableWebAssemblyStreaming: true,
                document: documentUrl,
                licenseKey: configService.get(Constants.Env.NutrientApiKey),
                baseUrl: `${window.location.protocol}//${window.location.host}/`,
                theme: PSPDFKit.Theme.DARK,
                initialViewState: new PSPDFKit.ViewState({
                    currentPageIndex: pageNumber - 1,
                    layoutMode: PSPDFKit.LayoutMode.SINGLE,
                }),
                editableAnnotationTypes: [],
                toolbarItems: [
                    ...PSPDFKit.defaultToolbarItems.filter((item) =>
                        ['pager', 'zoom-in', 'zoom-out', 'zoom-mode'].includes(item.type)
                    ),
                    { type: 'spacer' },
                    ...PSPDFKit.defaultToolbarItems.filter((item) =>
                        ['export-pdf', 'search', 'print'].includes(item.type)
                    ),
                ],
            }).then(async (instance) => {
                const annotations = (
                    await Promise.all(
                        Array.from({ length: highlightedWords.length }).map((element, index) =>
                            instance.search(highlightedWords[index])
                        )
                    )
                ).flatMap((searchResults) =>
                    searchResults.reduce((acc: any, searchResult) => {
                        const { rectsOnPage, pageIndex } = searchResult;

                        const bbox = new PSPDFKit.Geometry.Rect(rectsOnPage.get(0));

                        return acc.concat(
                            new PSPDFKit.Annotations.HighlightAnnotation({
                                id: PSPDFKit.generateInstantId(),
                                pageIndex: pageIndex,
                                boundingBox: bbox,
                                rects: rectsOnPage,
                                color: PSPDFKit.Color.LIGHT_BLUE,
                            })
                        );
                    }, [])
                );

                await instance.create(annotations);
            });
        })();

        return () => {
            if (PSPDFKit) {
                PSPDFKit.unload(container);
            }
        };
    }, []);

    return <div ref={containerRef} style={{ width: '100%', height: '80vh' }} />;
};
